import type { ServiceType } from '@/context/LeadContext';
import { getCsrfToken } from '@/integrations/php/client';

export interface AdminPartnerRecord {
  id: string;
  name: string;
  phone: string;
  email: string;
  location: string;
  miniImageUrl: string | null;
  miniDescription: string | null;
  services: ServiceType[];
  active: boolean;
  smsOptOut: boolean;
  latitude: number | null;
  longitude: number | null;
  radius: number;
  routingReady: boolean;
  emailReady: boolean;
  smsReady: boolean;
  portalReady: boolean;
  lastLoginAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  totalLeads: number;
  leads30d: number;
}

export interface AdminPartnerUpdate {
  name: string;
  phone: string;
  email: string;
  location: string;
  miniImageUrl: string | null;
  miniDescription: string | null;
  services: ServiceType[];
  active: boolean;
  smsOptOut: boolean;
  latitude: number | null;
  longitude: number | null;
  radius: number;
}

export class AdminPartnerApiError extends Error {
  readonly fields: Record<string, string>;

  constructor(message: string, fields: Record<string, string> = {}) {
    super(message);
    this.name = 'AdminPartnerApiError';
    this.fields = fields;
  }
}

const number = (value: unknown): number => Number(value || 0);
const nullableNumber = (value: unknown): number | null =>
  value === null || value === undefined || value === '' ? null : Number(value);
const nullableString = (value: unknown): string | null =>
  value === null || value === undefined || value === '' ? null : String(value);

function normalizePartner(value: unknown): AdminPartnerRecord {
  const partner = (value && typeof value === 'object' ? value : {}) as Record<string, unknown>;
  return {
    id: String(partner.id || ''),
    name: String(partner.name || ''),
    phone: String(partner.phone || ''),
    email: String(partner.email || ''),
    location: String(partner.location || ''),
    miniImageUrl: nullableString(partner.mini_image_url),
    miniDescription: nullableString(partner.mini_description),
    services: Array.isArray(partner.services) ? partner.services as ServiceType[] : [],
    active: Boolean(partner.active),
    smsOptOut: Boolean(partner.sms_opt_out),
    latitude: nullableNumber(partner.latitude),
    longitude: nullableNumber(partner.longitude),
    radius: number(partner.radius),
    routingReady: Boolean(partner.routing_ready),
    emailReady: Boolean(partner.email_ready),
    smsReady: Boolean(partner.sms_ready),
    portalReady: Boolean(partner.portal_ready),
    lastLoginAt: nullableString(partner.last_login_at),
    createdAt: nullableString(partner.created_at),
    updatedAt: nullableString(partner.updated_at),
    totalLeads: number(partner.total_leads),
    leads30d: number(partner.leads_30d),
  };
}

async function parseResponse(response: Response): Promise<AdminPartnerRecord> {
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new AdminPartnerApiError(
      body?.error?.message || 'Δεν ήταν δυνατή η φόρτωση του συνεργάτη.',
      body?.error?.fields && typeof body.error.fields === 'object' ? body.error.fields : {},
    );
  }
  return normalizePartner(body?.data?.partner);
}

export async function fetchAdminPartner(partnerId: string): Promise<AdminPartnerRecord> {
  const params = new URLSearchParams({ id: partnerId });
  const response = await fetch(`/api/admin/partner.php?${params}`, {
    credentials: 'include',
    headers: { 'X-CSRF-Token': await getCsrfToken() },
  });
  return parseResponse(response);
}

export async function createAdminPartner(values: AdminPartnerUpdate): Promise<AdminPartnerRecord> {
  const response = await fetch('/api/admin/partner.php', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': await getCsrfToken(),
    },
    body: JSON.stringify(values),
  });
  return parseResponse(response);
}

export async function updateAdminPartner(partnerId: string, values: AdminPartnerUpdate): Promise<AdminPartnerRecord> {
  const response = await fetch('/api/admin/partner.php', {
    method: 'PATCH',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': await getCsrfToken(),
    },
    body: JSON.stringify({ id: partnerId, ...values }),
  });
  return parseResponse(response);
}
